import { useCallback, useEffect, useState } from 'react';
import { LoadingModal } from '../../../components/shareable/loadingModal';
import { useDashboard } from '../../../context/dashboardContext';
import CatalogPagination from '../../../components/shareable/customerPagination';
import toast from 'react-hot-toast';
import { useSearch } from '../../../context/SearchContext';
import CatalogTable from './components/catalogTable';
import UpdateCatalogModal from './components/updateCatalogModal';

// Master Price List — the clean SKU catalog (~102 products). Mirrors the Modular Conveyor page:
// searchable, paginated table; row Actions -> "Update Price" opens a modal. Saving a price applies
// it to every image filename / orientation / variant of that product, instantly.
const MasterPriceList = () => {
  const { getCatalogProducts, updateCatalogPrice } = useDashboard();
  const { searchQuery, updateSearch } = useSearch();

  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    updateSearch('', 'master-price-list');
    return () => updateSearch('', '');
  }, [updateSearch]);

  const filterProducts = useCallback((products, query) => {
    if (!query.trim()) return products;
    const q = query.toLowerCase().trim();
    return products.filter(
      (p) =>
        (p.sku?.toLowerCase() || '').includes(q) ||
        (p.module_description?.toLowerCase() || '').includes(q) ||
        (p.kind?.toLowerCase() || '').includes(q)
    );
  }, []);

  const fetchProducts = useCallback(async (preservePage = false) => {
    setLoading(true);
    const res = await getCatalogProducts();
    if (res.success) {
      setAllProducts(res.products);
      setFilteredProducts(filterProducts(res.products, searchQuery));
    } else {
      toast.error(res.error || 'Failed to fetch price list');
    }
    setLoading(false);
    if (!preservePage) { /* keep page on refresh-after-save */ }
  }, [getCatalogProducts, filterProducts, searchQuery]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  useEffect(() => {
    if (allProducts.length > 0) setFilteredProducts(filterProducts(allProducts, searchQuery));
  }, [allProducts, filterProducts, searchQuery]);

  const openUpdateModal = (product) => {
    setSelectedProduct(product);
    setUpdateModalOpen(true);
  };

  const handleUpdatePrice = async (data) => {
    if (!selectedProduct) return;
    setLoading(true);
    const res = await updateCatalogPrice(selectedProduct.sku, data);
    if (res.success) {
      // reflect the saved values locally without a full reload jump
      setAllProducts((prev) => prev.map((p) => (p.sku === selectedProduct.sku ? { ...p, ...res.product } : p)));
      setLoading(false);
      toast.success(`${selectedProduct.sku} updated`);
      setUpdateModalOpen(false);
    } else {
      toast.error(res.error || 'Failed to update price');
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (page) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };

  return loading ? (
    <LoadingModal loading={loading} />
  ) : (
    <div className="p-6">
      <div className="relative mb-2 mt-3 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-red-700 uppercase tracking-wide">
          Master Price List {searchQuery && `(Filtered: ${filteredProducts.length} found)`}
        </h2>
      </div>
      <p className="mb-4 text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-md px-4 py-2">
        The single source of truth for pricing. Use a row's <span className="font-semibold">Actions → Update Price</span> —
        the change applies to every orientation and variant of that product, instantly.
      </p>

      <CatalogTable
        products={currentProducts}
        onUpdate={openUpdateModal}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
      />

      <div className="mt-4">
        <CatalogPagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          totalItems={filteredProducts.length}
          itemsPerPage={itemsPerPage}
        />
      </div>

      <UpdateCatalogModal
        isOpen={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        onSave={handleUpdatePrice}
        initialData={selectedProduct}
      />
    </div>
  );
};

export default MasterPriceList;
